import React, { Component } from "react";
import { withRouter } from "react-router-dom";
import PropTypes from "prop-types";
import withStyles from "@material-ui/core/styles/withStyles";
import CircularProgress from "@material-ui/core/CircularProgress";
import Dialog from "@material-ui/core/Dialog";
import DialogContent from "@material-ui/core/DialogContent";
import DialogFooter from "components/Dialog/DialogFooter.jsx";
import MenuItem from "@material-ui/core/MenuItem";
import Select from "@material-ui/core/Select";
import InputLabel from "@material-ui/core/InputLabel";
import FormControl from "@material-ui/core/FormControl";
import Icon from "@material-ui/core/Icon";

import GridContainer from "components/Grid/GridContainer.jsx";
import GridItem from "components/Grid/GridItem.jsx";
import Card from "components/Card/Card.jsx";
import CardHeader from "components/Card/CardHeader.jsx";
import CardBody from "components/Card/CardBody.jsx";
import Button from "components/CustomButtons/Button.jsx";
import CustomInput from "components/CustomInput/CustomInput.jsx";
import CustomDatePicker from "components/DatePicker/CustomDatePicker.jsx";
import DialogHeader from "components/Dialog/DialogHeader.jsx";
import DataTable, { Pill } from "components/DataTable/DataTable.jsx";

import api from "api/index";
import ViewToolbar from "components/Toolbar/ViewToolbar.jsx";

const STATUSES = ["open", "closed", "filled"];
const DEPARTMENTS = ["Academic", "Administration", "Finance", "Library", "Support", "ICT", "Estates"];

const STATUS_TONES = {
  open: "success",
  closed: "muted",
  filled: "info"
};

const BLANK_FORM = {
  position: "",
  department: "",
  postedDate: "",
  deadline: "",
  status: "open",
  applicantCount: ""
};

const styles = {
  header: { display: "flex", justifyContent: "space-between", alignItems: "center" },
  formControl: { width: "100%", marginTop: "16px" },
  errorMsg: { color: "#f44336", fontSize: "13px" },
  infoMsg: { color: "#1565c0", fontSize: "12px", background: "#e3f2fd", borderRadius: "4px", padding: "8px 12px", marginBottom: "12px" }
};

class RecruitmentList extends Component {
  state = {
    entries: [],
    loading: true,
    open: false,
    editing: null,
    form: { ...BLANK_FORM },
    saving: false,
    error: null,
    formError: null,
    usingLocalState: false
  };

  componentDidMount() {
    this.fetchEntries();
  }

  fetchEntries = async () => {
    this.setState({ loading: true, error: null });
    try {
      const res = await api.get("/recruitment");
      const d = res.data.data || res.data;
      this.setState({ entries: Array.isArray(d) ? d : d.data || [], loading: false, usingLocalState: false });
    } catch (e) {
      // Endpoint may not exist — fall back to local state
      this.setState({ loading: false, usingLocalState: true });
    }
  };

  handleChange = field => e => {
    const v = e.target.value;
    this.setState(prev => ({ form: { ...prev.form, [field]: v } }));
  };

  openAdd = () => {
    this.setState({ open: true, editing: null, form: { ...BLANK_FORM }, formError: null });
  };

  openEdit = entry => {
    this.setState({
      open: true,
      editing: entry.id,
      formError: null,
      form: {
        position: entry.position || "",
        department: entry.department || "",
        postedDate: entry.postedDate || "",
        deadline: entry.deadline || "",
        status: entry.status || "open",
        applicantCount: entry.applicantCount != null ? String(entry.applicantCount) : ""
      }
    });
  };

  handleSave = async () => {
    const { form, editing, usingLocalState } = this.state;
    if (!form.position || !form.department) {
      this.setState({ formError: "Position and department are required." });
      return;
    }
    this.setState({ saving: true, formError: null });

    if (usingLocalState) {
      // Local state only — no backend
      if (editing) {
        this.setState(prev => ({
          entries: prev.entries.map(e => e.id === editing ? { ...e, ...form, applicantCount: Number(form.applicantCount) || 0 } : e),
          open: false, saving: false
        }));
      } else {
        const newEntry = {
          ...form,
          id: Date.now(),
          applicantCount: Number(form.applicantCount) || 0,
          postedDate: form.postedDate || new Date().toISOString().slice(0, 10)
        };
        this.setState(prev => ({ entries: [newEntry, ...prev.entries], open: false, saving: false }));
      }
      return;
    }

    try {
      const payload = { ...form, applicantCount: Number(form.applicantCount) || 0 };
      if (editing) {
        await api.put(`/recruitment/${editing}`, payload);
      } else {
        await api.post("/recruitment", payload);
      }
      this.setState({ open: false, saving: false });
      this.fetchEntries();
    } catch (e) {
      // If save fails, fall back to local state update
      const payload = { ...form, applicantCount: Number(form.applicantCount) || 0 };
      if (editing) {
        this.setState(prev => ({
          entries: prev.entries.map(e => e.id === editing ? { ...e, ...payload } : e),
          open: false, saving: false, usingLocalState: true
        }));
      } else {
        const newEntry = { ...payload, id: Date.now(), postedDate: payload.postedDate || new Date().toISOString().slice(0, 10) };
        this.setState(prev => ({ entries: [newEntry, ...prev.entries], open: false, saving: false, usingLocalState: true }));
      }
    }
  };

  handleDelete = id => {
    if (!window.confirm("Remove this recruitment entry?")) return;
    this.setState(prev => ({ entries: prev.entries.filter(e => e.id !== id) }));
    if (!this.state.usingLocalState) {
      api.delete(`/recruitment/${id}`).catch(() => { /* silent */ });
    }
  };

  render() {
    const { classes } = this.props;
    const { entries, loading, open, editing, form, saving, error, formError, usingLocalState } = this.state;

    const columns = [
      { id: "position", header: "Position", accessorFn: e => e.position || "—" },
      { id: "department", header: "Department", accessorFn: e => e.department || "—" },
      { id: "postedDate", header: "Posted Date", accessorFn: e => e.postedDate || "—" },
      { id: "deadline", header: "Deadline", accessorFn: e => e.deadline || "—" },
      {
        id: "status",
        header: "Status",
        cell: info => {
          const status = info.row.original.status || "—";
          return <Pill tone={STATUS_TONES[status] || "muted"}>{status}</Pill>;
        }
      },
      {
        id: "applications",
        header: "Applications",
        accessorFn: e => (e.applicantCount != null ? String(e.applicantCount) : "0")
      },
      {
        id: "actions",
        header: "",
        cell: info => {
          const e = info.row.original;
          return (
            <div style={{ display: "flex", gap: "4px" }}>
              <Button size="sm" color="info" simple onClick={() => this.openEdit(e)}>Edit</Button>
              <Button size="sm" color="danger" simple onClick={() => this.handleDelete(e.id)}>Remove</Button>
            </div>
          );
        }
      }
    ];

    return (
      <GridContainer>
        <GridItem xs={12}>
          <Card>
            <CardHeader color="primary" style={{ margin: -8, padding: "24px 20px", display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                <div>
                  <h4 style={{ margin: 0, color: "#fff", fontWeight: "bold" }}>Recruitment Tracker</h4>
                  <p style={{ margin: "2px 0 0", color: "rgba(255,255,255,0.8)", fontSize: "13px" }}>
                    {entries.length} position{entries.length !== 1 ? "s" : ""}
                  </p>
                </div>
                <div style={{ display: "flex", gap: "8px" }}>
                  <Button size="sm" color="white" onClick={this.openAdd}>
                    <Icon style={{ marginRight: "4px", fontSize: "16px" }}>add</Icon> Add Position
                  </Button>
                </div>
            </CardHeader>
            <CardBody>
              <ViewToolbar tabs={[]} actions={[]} />
              {usingLocalState && (
                <div className={classes.infoMsg}>
                  <Icon style={{ fontSize: "14px", verticalAlign: "middle", marginRight: "6px" }}>info</Icon>
                  Recruitment API not available — data is stored locally for this session only.
                </div>
              )}
              {error && <p className={classes.errorMsg}>{error}</p>}

              <DataTable
                columns={columns}
                data={entries}
                isLoading={loading}
                emptyState="No recruitment entries found. Add a new position to get started."
              />
            </CardBody>
          </Card>
        </GridItem>

        <Dialog open={open} onClose={() => this.setState({ open: false })} maxWidth="sm" fullWidth>
          <DialogHeader title={editing ? "Edit Position" : "Add Open Position"} />
          <DialogContent>
            {formError && <p className={classes.errorMsg}>{formError}</p>}
            <GridContainer>
              <GridItem xs={12}>
                <CustomInput
                  labelText="Position *"
                  id="recPosition"
                  formControlProps={{ fullWidth: true }}
                  inputProps={{ value: form.position, onChange: this.handleChange("position"), placeholder: "e.g. Senior Tutor" }}
                />
              </GridItem>
              <GridItem xs={12} sm={6}>
                <FormControl className={classes.formControl}>
                  <InputLabel>Department *</InputLabel>
                  <Select value={form.department} onChange={this.handleChange("department")}>
                    <MenuItem value="">— Select —</MenuItem>
                    {DEPARTMENTS.map(d => <MenuItem key={d} value={d}>{d}</MenuItem>)}
                  </Select>
                </FormControl>
              </GridItem>
              <GridItem xs={12} sm={6}>
                <FormControl className={classes.formControl}>
                  <InputLabel>Status</InputLabel>
                  <Select value={form.status} onChange={this.handleChange("status")}>
                    {STATUSES.map(s => <MenuItem key={s} value={s}>{s.charAt(0).toUpperCase() + s.slice(1)}</MenuItem>)}
                  </Select>
                </FormControl>
              </GridItem>
              <GridItem xs={12} sm={6}>
                <CustomDatePicker
                  labelText="Posted Date"
                  id="recPostedDate"
                  formControlProps={{ fullWidth: true }}
                  inputProps={{ value: form.postedDate, onChange: this.handleChange("postedDate") }}
                />
              </GridItem>
              <GridItem xs={12} sm={6}>
                <CustomDatePicker
                  labelText="Application Deadline"
                  id="recDeadline"
                  formControlProps={{ fullWidth: true }}
                  inputProps={{ value: form.deadline, onChange: this.handleChange("deadline") }}
                />
              </GridItem>
              <GridItem xs={12} sm={6}>
                <CustomInput
                  labelText="Applicant Count"
                  id="recCount"
                  formControlProps={{ fullWidth: true }}
                  inputProps={{ value: form.applicantCount, onChange: this.handleChange("applicantCount"), type: "number", min: "0" }}
                />
              </GridItem>
            </GridContainer>
          </DialogContent>
          <DialogFooter>
            <Button outlined onClick={() => this.setState({ open: false })} disabled={saving}>Cancel</Button>
            <Button color="primary" onClick={this.handleSave} disabled={saving}>
              {saving ? <CircularProgress size={18} style={{ color: "#fff" }} /> : (editing ? "Update" : "Add Position")}
            </Button>
          </DialogFooter>
        </Dialog>
      </GridContainer>
    );
  }
}

RecruitmentList.propTypes = { classes: PropTypes.object.isRequired };
export default withRouter(withStyles(styles)(RecruitmentList));
